/**
 * DashScope Wan 2.6（视频）官方接口
 *
 * POST  /api/v1/services/aigc/video-generation/video-synthesis
 * GET   /api/v1/tasks/{taskId}
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var POLL_INTERVAL = 3000
var POLL_TIMEOUT = 10 * 60 * 1000

var POST_PATH = '/api/v1/services/aigc/video-generation/video-synthesis'

function getDashScopeWanConfig() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL || 'https://dashscope-intl.aliyuncs.com').replace(/\/$/, ''),
    apiKey: process.env.VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY || ''
  }
}

function createClient() {
  var cfg = getDashScopeWanConfig()
  if (!cfg.baseURL) throw new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_BASE_URL')
  if (!cfg.apiKey) throw new Error('未配置 VUE_APP_OFFICIAL_DASHSCOPE_WAN_API_KEY')

  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + cfg.apiKey,
      'X-DashScope-Async': 'enable'
    }
  })

  instance.interceptors.response.use(
    function (res) {
      return res.data
    },
    function (err) {
      var respData = err.response && err.response.data
      var msg =
        extractApiErrorMessage(respData) ||
        (respData && (respData.message || respData.messge)) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return instance
}

function normalizeSeed(seed) {
  var s = seed
  if (s === '' || s == null) s = -1
  s = parseInt(s, 10)
  if (isNaN(s)) s = -1
  if (s < -1) s = -1
  return s
}

function normalizeAspect(aspectRatio) {
  var v = String(aspectRatio || '').trim()
  var allowed = ['16:9', '9:16', '1:1', '4:3', '3:4']
  return allowed.indexOf(v) !== -1 ? v : '16:9'
}

function normalizeVideoRes(videoRes) {
  var v = String(videoRes || '').trim()
  if (v === '1080p' || v === '1080P') return '1080P'
  return '720P'
}

function resolveSize(params) {
  // Spec：2.6 仅支持 720/1080 + aspectRatio（1:1/4:3/3:4/16:9/9:16）
  var vr = normalizeVideoRes(params.videoRes)
  var ar = normalizeAspect(params.aspectRatio)

  if (vr === '720P') {
    if (ar === '9:16') return '720*1280'
    if (ar === '1:1') return '960*960'
    if (ar === '4:3') return '1088*832'
    if (ar === '3:4') return '832*1088'
    return '1280*720'
  }

  // vr !== '720P'，按 1080P 兜底
  if (ar === '9:16') return '1080*1920'
  if (ar === '1:1') return '1440*1440'
  if (ar === '4:3') return '1632*1248'
  if (ar === '3:4') return '1248*1632'
  return '1920*1080'
}

function normalizeDuration(duration) {
  var d = parseInt(duration, 10)
  if (d !== 5 && d !== 10 && d !== 15) d = 5
  return d
}

export function submitDashScopeWan26Task(params) {
  var client = createClient()
  var input = {
    prompt: (params.prompt || '').trim().slice(0, 2000)
  }

  // 负面提示词（可选）
  if (params.negativePrompt != null && String(params.negativePrompt).trim() !== '') {
    input.negative_prompt = String(params.negativePrompt).trim().slice(0, 2000)
  }

  var videoMode = params.videoMode || 'text'
  if (videoMode === 'image' && params.referenceImage) {
    input.img_url = String(params.referenceImage)
  }

  // 2.6 才有 audio_url
  if (params.generateAudio && params.audioUrl) {
    input.audio_url = String(params.audioUrl)
  }

  var parameters = {
    prompt_extend: true,
    duration: normalizeDuration(params.videoDuration),
    seed: normalizeSeed(params.seed)
  }
  if (videoMode === 'image') {
    // 图生视频：没有纵横比，仅按分辨率走 resolution
    parameters.resolution = normalizeVideoRes(params.videoRes)
  } else {
    // 文生视频：需要 size（含纵横比）
    parameters.size = resolveSize(params)
  }

  var body = {
    model: 'wan2.6-t2v',
    input: input,
    parameters: {
      prompt_extend: parameters.prompt_extend,
      duration: parameters.duration,
      seed: parameters.seed,
      resolution: parameters.resolution,
      size: parameters.size
    }
  }

  return client.post(POST_PATH, body).then(function (data) {
    var output = data && data.output ? data.output : null
    var id = output && (output.task_id || output.taskId || output.taskIdStr)
    if (!id) return Promise.reject(new Error('官方接口未返回任务 task_id'))
    return { taskId: String(id) }
  })
}

var DASHSCOPE_VIDEO_OSS_HOST = 'dashscope-result-sgp.oss-ap-southeast-1.aliyuncs.com'

function proxyDashScopeVideoUrl(url) {
  try {
    var u = new URL(url)
    if (u.host === DASHSCOPE_VIDEO_OSS_HOST) {
      return '/ossDashscopeProxy' + u.pathname + u.search
    }
  } catch (e) {}
  return url
}

function downloadVideoBlob(videoUrl) {
  var fetchUrl = proxyDashScopeVideoUrl(videoUrl)
  console.log('[Wan2.6] downloading video blob', { originalUrl: videoUrl, fetchUrl: fetchUrl })
  return fetch(fetchUrl)
    .then(function (res) {
      if (!res.ok) return Promise.reject(new Error('download video failed: HTTP ' + res.status))
      return res.blob()
    })
    .then(function (blob) {
      if (!blob || !blob.size) return Promise.reject(new Error('download video failed: empty blob'))
      console.log('[Wan2.6] video blob downloaded', blob.size, 'bytes')
      return blob
    })
}

function parseTaskStatus(data) {
  var output = data && data.output ? data.output : null
  var st = output && output.task_status ? String(output.task_status).toUpperCase() : ''
  var videoUrl = output && output.video_url ? String(output.video_url) : ''

  var msg = ''
  if (output) {
    msg =
      (output.message != null ? String(output.message) : '') ||
      (output.messge != null ? String(output.messge) : '') ||
      (output.error != null ? (typeof output.error === 'string' ? output.error : output.error.message) : '') ||
      ''
  }

  if (st === 'SUCCEEDED') {
    if (!videoUrl) return { status: 'failed', url: null, error: msg || '未返回 video_url' }
    return { status: 'completed', url: videoUrl, error: null }
  }

  if (st === 'FAILED' || st === 'CANCELED') {
    return { status: 'failed', url: null, error: msg || '生成失败' }
  }

  if (st === 'PENDING' || st === 'RUNNING') {
    return { status: 'processing', url: null, error: null }
  }

  if (st === 'UNKNOWN') {
    return { status: 'failed', url: null, error: msg || '任务不存在或状态未知' }
  }

  return { status: 'processing', url: null, error: null }
}

export function pollDashScopeWan26Task(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false

  var client = createClient()
  var path = '/api/v1/tasks/' + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(path)
      .then(function (data) {
        if (stopped) return
        var parsed = parseTaskStatus(data)

        if (parsed.status === 'completed' && parsed.url) {
          downloadVideoBlob(parsed.url)
            .then(function (blob) {
              parsed.videoBlob = blob
              if (typeof callback === 'function') callback(null, parsed)
              stop()
            })
            .catch(function (dlErr) {
              console.warn('[Wan2.6] download blob failed, using original url', dlErr)
              if (typeof callback === 'function') callback(null, parsed)
              stop()
            })
          return
        }

        if (typeof callback === 'function') {
          callback(null, parsed)
        }
        if (parsed.status === 'failed') {
          stop()
          return
        }
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}

